<script setup lang="ts">
import LeIconButton from "@dsk/components/IconButton/LeIconButton.vue";
import LeCheckmark from "@dsk/icons/LeCheckmark.vue";
import LeCross from "@dsk/icons/LeCross.vue";
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import TransitionExpanded from "@/components/transitions/TransitionExpanded.vue";
import { useSingleton } from "@/composables";
import { NoteService } from "@/services";
import { useNotesStore, useSearchStore } from "@/stores";
import type { Note } from "@/types";

const props = defineProps<{
  methodId: string;
}>();

const searchStore = useSearchStore();
const notesStore = useNotesStore();

const { t } = useI18n();

const NOTE_CHAR_LIMIT = 280;

const input = ref<HTMLTextAreaElement>();
const note = ref<string>("");
const cachedNote = ref<string>("");
const charCount = computed<number>(() => note.value.length);
const isSavingBlocked = computed<boolean>(() => charCount.value > NOTE_CHAR_LIMIT || cachedNote.value == note.value);

const searchWord = ref<string>("");

onMounted(async () => {
  searchWord.value = searchStore.query;

  const savedNote: Note = await getNote(props.methodId, searchWord.value);

  if (savedNote.text) {
    note.value = savedNote.text;
    cachedNote.value = savedNote.text;
    notesStore.open();
  }
});

watch(
  () => note.value,
  () => nextTick(() => updateInputSize()),
  { immediate: true }
);

const updateInputSize = () => {
  if (!input.value) {
    return;
  }

  // Reset height, so we can measure how long the input is,
  // This handles when user deletes lines.
  input.value.style.height = "90px";
  input.value.style.height = `${input.value.scrollHeight}px`;
};

const getNote = useSingleton(NoteService.getNote);

const cancelNote = () => {
  note.value = cachedNote.value;
};

const saveNote = () => {
  if (isSavingBlocked.value) {
    return;
  }

  cachedNote.value = note.value;
  NoteService.setNote(props.methodId, searchWord.value, note.value);
};
</script>

<template>
  <TransitionExpanded :open="notesStore.isOpen">
    <div class="le-note">
      <div class="le-note__top">
        <div class="le-note__top-text">
          <p class="le-note__title">{{ t("le-note.my-notes") }}</p>
          <p class="le-note__limit" :class="{ 'le-note__limit--error': charCount > NOTE_CHAR_LIMIT }">
            {{ charCount }}/{{ NOTE_CHAR_LIMIT }}
          </p>
        </div>
        <div class="le-note__button-group">
          <LeIconButton
            class="le-note__button"
            color="error"
            :class="{ 'le-note__button--disabled': isSavingBlocked }"
            size="l"
            @click="cancelNote()"
          >
            <LeCross />
          </LeIconButton>
          <LeIconButton
            class="le-note__button"
            :class="{ 'le-note__button--disabled': isSavingBlocked }"
            color="primary"
            variant="dark"
            size="l"
            @click="saveNote()"
          >
            <LeCheckmark />
          </LeIconButton>
        </div>
      </div>
      <div>
        <textarea ref="input" v-model="note" class="le-note__input"></textarea>
      </div>
    </div>
  </TransitionExpanded>
</template>

<style scoped lang="less">
@import "@dsk/styles/all.less";

.le-note {
  margin-bottom: 32px;
  padding: 32px;
  border-radius: 4px;
  background-color: @colors[@primary][100];
  box-shadow: @shadows[floating-default];

  &__top {
    display: grid;
    grid-template-columns: 2fr 1fr;
    margin-bottom: 8px;

    &-text {
      display: grid;
      grid-template-columns: 1fr;
      align-items: flex-end;

      #media-breakpoint-up(sm, {
        grid-template-columns: auto 1fr;
      });
    }
  }

  &__title {
    margin-right: 8px;
    margin-bottom: 0;
    color: @colors[@neutrals][500];
    font-family: @font-family-heading;
    font-size: 20px;
    line-height: 28px;
  }

  &__limit {
    margin-bottom: 0;
    color: @colors[@neutrals][300];
    font-size: 12px;
    font-style: italic;

    &--error {
      color: @colors[@error][600];
    }
  }

  &__button-group {
    display: flex;
    justify-content: flex-end;

    & > :first-child {
      margin-right: 12px;
    }
  }

  &__button {
    &--disabled {
      cursor: not-allowed;
      user-select: none;
    }
  }

  &__input {
    min-width: 100%;
    max-width: 100%;
    min-height: 90px;
    padding: 0;
    border: none;
    background-color: @colors[@primary][100];
    background-image: -webkit-linear-gradient(
      bottom,
      @colors[@secondary][500] 0,
      rgba(128, 128, 128, 0) 4%,
      rgba(128, 128, 128, 0) 100%
    );
    background-position: 0 -1px;
    background-size: auto 30px;
    line-height: 30px;
    resize: none;
  }
}
</style>
